import type { JSONSchemaDefinition } from "@forgepoint/shared";
import type { JSONSchema7, JSONSchema7Definition } from "json-schema";
import type { JSONObject } from "@/components/jsonSchemaForm/types";

/**
 * Returns a soft-warning message when the saved `pipelineId` in the sync's
 * config no longer appears in the resolved schema's `pipelineId.enum`. The
 * sync's previous selection is left in place so the user can re-pick a valid
 * pipeline rather than losing their configuration silently.
 * @param resolvedSchema - The schema returned by the resolved-schema endpoint
 * @param config - The current form config
 * @returns A warning string, or null when no warning is required
 */
export function buildMissingPipelineWarning(
  resolvedSchema: JSONSchema7 | null,
  config: JSONObject,
): string | null {
  if (!resolvedSchema || resolvedSchema.type !== "object") {
    return null;
  }
  const properties = resolvedSchema.properties;
  if (!properties) {
    return null;
  }
  const pipelineProperty: JSONSchema7Definition | undefined =
    properties.pipelineId;
  if (!pipelineProperty || typeof pipelineProperty === "boolean") {
    return null;
  }
  const allowed = pipelineProperty.enum;
  if (!Array.isArray(allowed) || allowed.length === 0) {
    return null;
  }
  const savedPipelineId = config.pipelineId;
  if (typeof savedPipelineId !== "string" || savedPipelineId.length === 0) {
    return null;
  }
  if (allowed.some((option) => option === savedPipelineId)) {
    return null;
  }
  return "The HubSpot pipeline this sync was configured against no longer exists. Pick a new pipeline.";
}

/**
 * Returns a clone of the resolved HubSpot sync-config schema where each
 * `opportunityCustomFieldMapping.<field>` enum excludes HubSpot property names
 * already chosen for *other* custom fields. The current value of each field is
 * preserved so the form stays valid; uniqueness is enforced strictly across
 * the rest of the mapping.
 *
 * No-op for schemas that don't have an `opportunityCustomFieldMapping` object
 * with enumerated properties.
 *
 * @param schema - The resolved sync-config schema, or null
 * @param config - The current form config (carries the in-progress opportunityCustomFieldMapping)
 * @returns A new schema with filtered enums, or the input schema unchanged
 */
export function filterCustomFieldMappingEnumsByCurrentConfig(
  schema: JSONSchema7 | null,
  config: JSONObject,
): JSONSchema7 | null {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return schema;
  }

  const opportunityCustomFieldMappingDef =
    schema.properties.opportunityCustomFieldMapping;
  if (
    !isObjectSchema(opportunityCustomFieldMappingDef) ||
    !opportunityCustomFieldMappingDef.properties
  ) {
    return schema;
  }

  const currentMapping = isJSONObject(config.opportunityCustomFieldMapping)
    ? config.opportunityCustomFieldMapping
    : {};

  const crossSectionUsed = collectUsedProperties(
    isJSONObject(config.opportunityFieldMapping)
      ? config.opportunityFieldMapping
      : {},
  );

  const filteredProps: Record<string, JSONSchema7Definition> = {};
  for (const [fieldKey, propDef] of Object.entries(
    opportunityCustomFieldMappingDef.properties,
  )) {
    filteredProps[fieldKey] = filterEnumForField(
      fieldKey,
      propDef,
      currentMapping,
      crossSectionUsed,
    );
  }

  return {
    ...schema,
    properties: {
      ...schema.properties,
      opportunityCustomFieldMapping: {
        ...opportunityCustomFieldMappingDef,
        properties: filteredProps,
      },
    },
  };
}

/**
 * Filters one field mapping property's `enum`/`enumNames` to remove
 * HubSpot property names already picked by *other* fields in the current
 * mapping, plus any cross-section reserved properties.
 * @param fieldKey - The FP field name this dropdown represents
 * @param propDef - The schema definition for this field's HubSpot property picker
 * @param currentMapping - The in-progress mapping object from the form
 * @param crossSectionUsed - HS property names reserved by another mapping section
 * @returns The schema definition, with enum/enumNames trimmed
 */
function filterEnumForField(
  fieldKey: string,
  propDef: JSONSchema7Definition,
  currentMapping: JSONObject,
  crossSectionUsed: Set<string>,
): JSONSchema7Definition {
  if (typeof propDef === "boolean" || !Array.isArray(propDef.enum)) {
    return propDef;
  }

  const usedByOthers = new Set<string>(crossSectionUsed);
  for (const [otherKey, value] of Object.entries(currentMapping)) {
    if (otherKey === fieldKey) {
      continue;
    }
    // Handle both plain string mappings and select field object mappings
    if (typeof value === "string" && value.length > 0) {
      usedByOthers.add(value);
    } else if (
      isJSONObject(value) &&
      typeof value.erpProperty === "string" &&
      value.erpProperty.length > 0
    ) {
      usedByOthers.add(value.erpProperty);
    }
  }

  if (usedByOthers.size === 0) {
    return propDef;
  }

  const extended: JSONSchemaDefinition = propDef;
  const enumNames = Array.isArray(extended.enumNames)
    ? extended.enumNames
    : null;

  const filteredEnum: typeof propDef.enum = [];
  const filteredEnumNames: string[] = [];
  propDef.enum.forEach((option, index) => {
    if (typeof option === "string" && usedByOthers.has(option)) {
      return;
    }
    filteredEnum.push(option);
    if (enumNames) {
      filteredEnumNames.push(enumNames[index] ?? String(option));
    }
  });

  const next: JSONSchemaDefinition = {
    ...propDef,
    enum: filteredEnum,
  };
  if (enumNames) {
    next.enumNames = filteredEnumNames;
  }
  return next;
}

/**
 * Returns a clone of the resolved HubSpot sync-config schema where each
 * `opportunityFieldMapping.<field>` enum excludes HubSpot property names
 * already chosen for *other* built-in fields. Cross-section uniqueness is
 * also enforced against `opportunityCustomFieldMapping`.
 *
 * No-op for schemas that don't have an `opportunityFieldMapping` object
 * with enumerated properties.
 *
 * @param schema - The resolved sync-config schema, or null
 * @param config - The current form config (carries the in-progress opportunityFieldMapping)
 * @returns A new schema with filtered enums, or the input schema unchanged
 */
export function filterOpportunityFieldMappingEnumsByCurrentConfig(
  schema: JSONSchema7 | null,
  config: JSONObject,
): JSONSchema7 | null {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return schema;
  }

  const opportunityFieldMappingDef = schema.properties.opportunityFieldMapping;
  if (
    !isObjectSchema(opportunityFieldMappingDef) ||
    !opportunityFieldMappingDef.properties
  ) {
    return schema;
  }

  const currentMapping = isJSONObject(config.opportunityFieldMapping)
    ? config.opportunityFieldMapping
    : {};

  const crossSectionUsed = collectUsedProperties(
    isJSONObject(config.opportunityCustomFieldMapping)
      ? config.opportunityCustomFieldMapping
      : {},
  );

  const filteredProps: Record<string, JSONSchema7Definition> = {};
  for (const [fieldKey, propDef] of Object.entries(
    opportunityFieldMappingDef.properties,
  )) {
    filteredProps[fieldKey] = filterEnumForField(
      fieldKey,
      propDef,
      currentMapping,
      crossSectionUsed,
    );
  }

  return {
    ...schema,
    properties: {
      ...schema.properties,
      opportunityFieldMapping: {
        ...opportunityFieldMappingDef,
        properties: filteredProps,
      },
    },
  };
}

/**
 * Collects all HubSpot property names currently used in a mapping object.
 * Handles both plain string values and select field object mappings
 * (`{ erpProperty }`).
 * @param mapping - A field mapping object from the form config
 * @returns A set of used HubSpot property names
 */
function collectUsedProperties(mapping: JSONObject): Set<string> {
  const used = new Set<string>();
  for (const value of Object.values(mapping)) {
    if (typeof value === "string" && value.length > 0) {
      used.add(value);
    } else if (
      isJSONObject(value) &&
      typeof value.erpProperty === "string" &&
      value.erpProperty.length > 0
    ) {
      used.add(value.erpProperty);
    }
  }
  return used;
}

/**
 * Type guard: returns true when the value is a non-array, non-null JSONObject.
 * @param value - Any JSON value
 * @returns Whether value is a plain JSON object
 */
function isJSONObject(value: unknown): value is JSONObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Type guard: returns true when the schema definition is an `object` schema
 * (i.e. has a `properties` map we can descend into).
 * @param def - Any JSON schema definition
 * @returns Whether the definition is a plain object schema
 */
function isObjectSchema(
  def: JSONSchema7Definition | undefined,
): def is JSONSchema7 {
  return (
    typeof def === "object" &&
    def !== null &&
    !Array.isArray(def) &&
    def.type === "object"
  );
}
